/**
 * Every number on this dashboard, explained in one place.
 *
 * Two reasons this is a module and not a prop written at each call site:
 *
 *  1. The same figure appears on the overview, the statement and the ledger. If
 *     each screen carried its own wording they would drift, and a client reading
 *     two different definitions of "reserved" has no way to know which is true.
 *  2. Wording kept in the markup is wording that gets forgotten when a term is
 *     added. `glossary.test.ts` fails when a key is defined here and never used,
 *     or used and never defined — so a new number cannot ship unexplained.
 *
 * The statement page is deliberately NOT wired to this. It already explains each
 * row inline, and it is a page people print — an explanation that only exists in a
 * tooltip is an explanation missing from the printed copy.
 *
 * House style: say what it IS, then what it means for the client. No jargon, no
 * restating the label with longer words ("available credits are the credits that
 * are available" explains nothing).
 */
export const GLOSSARY = {
  available: {
    term: "Available credits",
    body: "What you can spend right now. It is this month's pool less the work already delivered and the work approved but not yet finished.",
  },
  spent: {
    term: "Credits spent",
    body: "Work that has been delivered and billed against the retainer this month. Once work is delivered its price is frozen, so a later change to the rate card never restates it.",
  },
  reserved: {
    term: "Credits reserved",
    body: "Work Prismic has approved and started, but not yet delivered. The credits are held so they cannot be spent twice. If a request is cancelled they come straight back.",
  },
  totalPool: {
    term: "Total monthly pool",
    body: "Everything you have to spend this month: the monthly allocation from the retainer, plus anything carried forward from last month, plus any credits added separately.",
  },
  allocation: {
    term: "Monthly allocation",
    body: "The credits the retainer issues each month. Fixed by the contract, and they arrive whether or not the previous month was used up.",
  },
  carriedIn: {
    term: "Carried forward",
    body: "Credits left unused at the end of last month. Nothing expires — they roll into this month automatically.",
  },
  topups: {
    term: "Additional credits",
    body: "Credits added outside the monthly allocation, by agreement. Only Prismic management can create them, and every one is recorded in the ledger with who approved it.",
  },
  pace: {
    term: "Usual pace",
    body: "The average credits used per completed month. Months that have not happened yet are excluded, and so is the current month, because a month still running would drag the average down and make the pace look slower than it is.",
  },
  forecast: {
    term: "Likely unused",
    body: "What would be left at the end of the contract if the usual pace continued: everything still to be issued, plus what is unused now, less the projected spend. It is an estimate from past months, not a commitment.",
  },
  thisVsLast: {
    term: "This month against last",
    body: "Credits delivered this month compared with the whole of last month. This month is usually still running, so early in the month expect it to read low.",
  },
} as const;

export type GlossaryKey = keyof typeof GLOSSARY;
